"use client";

import {
  ChevronRight,
  Sparkles,
  ArrowLeft,
  FileText,
  Calendar,
  Mail,
  Shield,
  Lock,
  Eye,
  Users,
  Globe,
  AlertTriangle,
} from "lucide-react";
import { useState } from "react";

import { Link } from "@/shared/ui/design-system/link.js";

// Header Component (reused from landing)
const Header = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);

  return (
    <header className="fixed top-0 right-0 left-0 z-50 border-b border-gray-800/50 bg-black/70 backdrop-blur-md">
      <div className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8">
        <nav className="flex h-14 items-center justify-between">
          <Link href="/" className="flex items-center gap-2">
            <div className="flex h-7 w-7 items-center justify-center rounded-lg bg-white">
              <Sparkles className="h-4 w-4 text-black" />
            </div>
            <span className="text-lg font-semibold text-white">NovioAI</span>
          </Link>

          <div className="hidden items-center gap-8 md:flex">
            <Link
              href="/#features"
              className="text-sm text-gray-400 transition-colors hover:text-white"
            >
              Возможности
            </Link>
            <Link
              href="/#pricing"
              className="text-sm text-gray-400 transition-colors hover:text-white"
            >
              Тарифы
            </Link>
            <button
              type="button"
              className="rounded-lg bg-white px-4 py-1.5 text-sm font-medium text-black transition-colors hover:bg-gray-100 disabled:cursor-not-allowed disabled:opacity-50"
              disabled
            >
              Скоро
            </button>
            {/* <button type="button" className="bg-white text-black px-4 py-1.5 rounded-lg text-sm font-medium hover:bg-gray-100 transition-colors">
              Попробовать
            </button> */}
          </div>

          <button
            type="button"
            aria-label="Open navigation menu"
            className="text-gray-400 transition-colors hover:text-white md:hidden"
            onClick={() => setIsMenuOpen(!isMenuOpen)}
          >
            <svg
              className="h-5 w-5"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M4 6h16M4 12h16M4 18h16"
              />
            </svg>
          </button>
        </nav>

        {isMenuOpen && (
          <div className="border-t border-gray-800/50 py-4 md:hidden">
            <div className="flex flex-col gap-3">
              <Link
                href="/#features"
                className="px-4 py-2 text-sm text-gray-400 transition-colors hover:text-white"
              >
                Функции
              </Link>
              <Link
                href="/#pricing"
                className="px-4 py-2 text-sm text-gray-400 transition-colors hover:text-white"
              >
                Цены
              </Link>
              <button
                type="button"
                className="mx-4 mt-2 rounded-lg bg-white px-4 py-2 text-sm font-medium text-black"
              >
                Попробовать
              </button>
            </div>
          </div>
        )}
      </div>
    </header>
  );
};

// Table of Contents Component
const TableOfContents = ({
  sections,
  activeSection,
}: {
  sections: { id: string; title: string }[];
  activeSection: string;
}) => (
  <div className="sticky top-20 rounded-xl border border-gray-800/50 bg-gray-900/50 p-6 backdrop-blur-sm">
    <h3 className="mb-4 flex items-center gap-2 text-lg font-semibold text-white">
      <FileText className="h-5 w-5" />
      Содержание
    </h3>
    <ul className="space-y-2">
      {sections.map((section) => (
        <li key={section.id}>
          <Link
            href={`#${section.id}`}
            className={`flex items-center gap-2 py-1 text-sm transition-colors hover:text-white ${
              activeSection === section.id ? "text-white" : "text-gray-400"
            }`}
          >
            <ChevronRight className="h-3 w-3" />
            {section.title}
          </Link>
        </li>
      ))}
    </ul>
  </div>
);

// Section Component
const Section = ({
  id,
  title,
  children,
}: {
  id: string;
  title: string;
  children: React.ReactNode;
}) => (
  <section id={id} className="mb-12 scroll-mt-24">
    <h2 className="mb-6 border-b border-gray-800 pb-2 text-2xl font-semibold text-white">
      {title}
    </h2>
    <div className="space-y-4 leading-relaxed text-gray-300">{children}</div>
  </section>
);

const PrivacyPolicyPage = () => {
  const activeSection = "general";

  const sections = [
    { id: "general", title: "1. Общие положения" },
    { id: "data-collection", title: "2. Какие данные мы собираем" },
    { id: "legal-basis", title: "3. Основания обработки" },
    { id: "data-sharing", title: "4. Передача данных третьим лицам" },
    { id: "user-rights", title: "5. Права пользователя" },
    { id: "security", title: "6. Безопасность данных" },
    { id: "children", title: "7. Конфиденциальность детей" },
    { id: "changes", title: "8. Уведомления об изменениях" },
    { id: "contact", title: "9. Контактная информация" },
  ];

  return (
    <div className="min-h-screen bg-black text-white">
      <Header />

      {/* Hero Section */}
      <section className="bg-gradient-to-b from-gray-950 to-black pt-20 pb-16">
        <div className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8">
          <div className="mb-12 text-center">
            <Link
              href="/"
              className="mb-6 inline-flex items-center gap-2 text-gray-400 transition-colors hover:text-white"
            >
              <ArrowLeft className="h-4 w-4" />
              Вернуться на главную
            </Link>

            <h1 className="mb-4 bg-gradient-to-br from-white to-gray-400 bg-clip-text text-4xl font-semibold tracking-tight text-transparent md:text-5xl">
              Политика конфиденциальности
            </h1>
            <p className="mb-8 text-xl text-gray-400">
              Как мы обрабатываем и защищаем ваши персональные данные
            </p>

            <div className="flex flex-col items-center justify-center gap-4 text-sm text-gray-500 sm:flex-row">
              <div className="flex items-center gap-2">
                <Calendar className="h-4 w-4" />
                <span>Дата вступления в силу: 03.07.2025</span>
              </div>
              <div className="hidden h-1 w-1 rounded-full bg-gray-600 sm:block" />
              <div className="flex items-center gap-2">
                <Shield className="h-4 w-4" />
                <span>Соответствие 152-ФЗ</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Main Content */}
      <div className="mx-auto max-w-6xl px-4 py-16 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 gap-8 lg:grid-cols-4">
          {/* Table of Contents */}
          <div className="lg:col-span-1">
            <TableOfContents
              sections={sections}
              activeSection={activeSection}
            />
          </div>

          {/* Content */}
          <div className="lg:col-span-3">
            <div className="rounded-xl border border-gray-800/50 bg-gray-900/30 p-8 backdrop-blur-sm">
              <Section id="general" title="1. Общие положения">
                <p>
                  Настоящая Политика в отношении обработки персональных данных
                  разработана индивидуальным предпринимателем Стрельниковым
                  Станиславом Дмитриевичем (далее — «Оператор», «мы»).
                </p>
                <p>
                  Цель Политики — информирование пользователей о порядке
                  обработки их персональных данных и обеспечение защиты их прав.
                </p>
                <p>
                  Использование Сервиса «NovioAI Assistant» означает
                  безоговорочное согласие с данной Политикой и условиями
                  обработки персональных данных.
                </p>
                <div className="mt-4 rounded-lg border border-blue-800/50 bg-blue-900/20 p-4">
                  <p className="flex items-start gap-2 text-blue-300">
                    <Shield className="mt-0.5 h-5 w-5 flex-shrink-0" />
                    <span>
                      Работа Оператора осуществляется с учетом принципов,
                      предусмотренных требованиями Федерального закона «О
                      персональных данных» в Российской Федерации —{" "}
                      <strong className="text-white">№152-ФЗ</strong> от 27 июля
                      2006 года. (далее №152-ФЗ): законности, добросовестности,
                      прозрачности, минимизации данных, ограничения целей.
                    </span>
                  </p>
                </div>
              </Section>

              <Section id="data-collection" title="2. Какие данные мы собираем">
                <p>
                  Мы обрабатываем персональные данные пользователей в
                  соответствии с Федеральным законом от 27.07.2006 № 152-ФЗ «О
                  персональных данных» и только в указанных ниже целях.
                </p>
                <div className="mt-6 space-y-6">
                  <div className="rounded-lg bg-gray-800/30 p-6">
                    <h4 className="mb-3 flex items-center gap-2 font-semibold text-white">
                      <Users className="h-5 w-5" />
                      1. Данные при регистрации
                    </h4>
                    <p className="mb-4 text-sm text-gray-400">
                      <strong>Основание:</strong> Для исполнения договора (ст. 6
                      № 152-ФЗ)
                    </p>
                    <h5 className="mb-2 font-semibold text-gray-200">
                      Собираемые данные:
                    </h5>
                    <ul className="mb-4 list-disc space-y-1 pl-5 text-sm">
                      <li>Имя</li>
                      <li>Адрес электронной почты</li>
                    </ul>
                    <h5 className="mb-2 font-semibold text-gray-200">
                      Цели обработки:
                    </h5>
                    <ul className="list-disc space-y-1 pl-5 text-sm">
                      <li>Создание и ведение учетной записи</li>
                      <li>Идентификация пользователя</li>
                      <li>Обеспечение безопасности аккаунта</li>
                      <li>Связь с пользователем (уведомления, поддержка)</li>
                    </ul>
                  </div>

                  <div className="rounded-lg bg-gray-800/30 p-6">
                    <h4 className="mb-3 flex items-center gap-2 font-semibold text-white">
                      <FileText className="h-5 w-5" />
                      2. Контент пользователя
                    </h4>
                    <p className="mb-4 text-sm text-gray-400">
                      <strong>Основание:</strong> Для исполнения договора (ст. 6
                      № 152-ФЗ)
                    </p>
                    <h5 className="mb-2 font-semibold text-gray-200">
                      Собираемые данные:
                    </h5>
                    <ul className="mb-4 list-disc space-y-1 pl-5 text-sm">
                      <li>
                        Любые данные, которые пользователь вводит, загружает или
                        передает через Сервис (текст, содержимое экрана, аудио)
                      </li>
                    </ul>
                    <h5 className="mb-2 font-semibold text-gray-200">
                      Цели обработки:
                    </h5>
                    <ul className="list-disc space-y-1 pl-5 text-sm">
                      <li>Анализ и обработка запросов с помощью AI</li>
                      <li>Предоставление функциональности сервиса</li>
                    </ul>
                  </div>

                  <div className="rounded-lg bg-gray-800/30 p-6">
                    <h4 className="mb-3 flex items-center gap-2 font-semibold text-white">
                      <Eye className="h-5 w-5" />
                      3. История переписок с AI
                    </h4>
                    <p className="mb-4 text-sm text-gray-400">
                      <strong>Основание:</strong> Для исполнения договора (ст. 6
                      № 152-ФЗ)
                    </p>
                    <h5 className="mb-2 font-semibold text-gray-200">
                      Собираемые данные:
                    </h5>
                    <ul className="mb-4 list-disc space-y-1 pl-5 text-sm">
                      <li>Текстовые логи взаимодействий</li>
                    </ul>
                    <h5 className="mb-2 font-semibold text-gray-200">
                      Цели обработки:
                    </h5>
                    <ul className="list-disc space-y-1 pl-5 text-sm">
                      <li>
                        Обеспечение доступа пользователя к истории переписки
                      </li>
                      {/* Planned: delete history in settings */}
                      {/* <li>Возможность удаления истории в настройках</li> */}
                    </ul>
                    <div className="mt-4 rounded-lg border border-green-800/50 bg-green-900/20 p-3">
                      <p className="text-sm text-green-300">
                        <strong>Важно:</strong> Эти данные не передаются третьим
                        лицам и не используются для обучения наших или сторонних
                        AI-моделей.
                      </p>
                    </div>
                  </div>

                  <div className="rounded-lg bg-gray-800/30 p-6">
                    <h4 className="mb-3 flex items-center gap-2 font-semibold text-white">
                      <Lock className="h-5 w-5" />
                      4. Платежная информация
                    </h4>
                    <p className="mb-4 text-sm text-gray-400">
                      <strong>Основание:</strong> Для исполнения договора (ст. 6
                      № 152-ФЗ)
                    </p>
                    <h5 className="mb-2 font-semibold text-gray-200">
                      Собираемые данные:
                    </h5>
                    <ul className="mb-4 list-disc space-y-1 pl-5 text-sm">
                      <li>
                        Статус платежа (от стороннего сервиса CloudPayments)
                      </li>
                    </ul>
                    <h5 className="mb-2 font-semibold text-gray-200">
                      Цели обработки:
                    </h5>
                    <ul className="list-disc space-y-1 pl-5 text-sm">
                      <li>Подтверждение оплаты услуг</li>
                      <li>Управление подписками и тарифами</li>
                    </ul>
                    <div className="mt-4 rounded-lg border border-amber-800/50 bg-amber-900/20 p-3">
                      <p className="text-sm text-amber-300">
                        <strong>Важно:</strong> Мы не храним данные банковских
                        карт — платежи обрабатываются через CloudPayments.
                      </p>
                    </div>
                  </div>

                  <div className="rounded-lg bg-gray-800/30 p-6">
                    <h4 className="mb-3 flex items-center gap-2 font-semibold text-white">
                      <Globe className="h-5 w-5" />
                      5. Технические и анонимные данные
                    </h4>
                    <p className="mb-4 text-sm text-gray-400">
                      <strong>Основание:</strong> Для улучшения сервиса (ст. 6 №
                      152-ФЗ, с согласия пользователя)
                    </p>
                    <h5 className="mb-2 font-semibold text-gray-200">
                      Собираемые данные:
                    </h5>
                    <ul className="mb-4 list-disc space-y-1 pl-5 text-sm">
                      <li>IP-адрес</li>
                      <li>Версия ОС и браузера</li>
                      <li>Версия Сервиса</li>
                      <li>Статистика использования</li>
                      <li>Отчёты о сбоях</li>
                    </ul>
                    <h5 className="mb-2 font-semibold text-gray-200">
                      Цели обработки:
                    </h5>
                    <ul className="list-disc space-y-1 pl-5 text-sm">
                      <li>Анализ работы сервиса</li>
                      <li>Повышение стабильности и производительности</li>
                      <li>Разработка новых функций</li>
                    </ul>
                    <div className="mt-4 rounded-lg border border-blue-800/50 bg-blue-900/20 p-3">
                      <p className="text-sm text-blue-300">
                        <strong>Контроль:</strong> Пользователь может ограничить
                        сбор этих данных в настройках.
                      </p>
                    </div>
                  </div>
                </div>
              </Section>

              <Section id="legal-basis" title="3. Основания обработки">
                <div className="rounded-lg bg-gray-800/30 p-4">
                  <p>
                    Обработка осуществляется на основании{" "}
                    <strong className="text-white">договорных отношений</strong>{" "}
                    (п. 5, ст. 6 152‑ФЗ) и с{" "}
                    <strong className="text-white">
                      согласия пользователя
                    </strong>{" "}
                    (п. 1, ст. 6 152‑ФЗ).
                  </p>
                </div>
              </Section>

              <Section
                id="data-sharing"
                title="4. Передача данных третьим лицам и трансграничная передача"
              >
                <div className="space-y-4">
                  <div className="rounded-lg border border-amber-800/50 bg-amber-900/20 p-4">
                    <p className="flex items-start gap-2 text-amber-300">
                      <AlertTriangle className="mt-0.5 h-5 w-5 flex-shrink-0" />
                      <span>
                        <strong>Важно:</strong> Для корректной работы Сервиса мы
                        передаём контент пользователя сторонним AI-провайдерам
                      </span>
                    </p>
                  </div>
                  <ul className="list-disc space-y-2 pl-6">
                    <li>
                      <strong className="text-white">
                        Сторонние AI-провайдеры:
                      </strong>{" "}
                      OpenAI, Anthropic, Google, а также другие сервисы
                      обработки аудио/текста
                    </li>
                    <li>
                      <strong className="text-white">Цель передачи:</strong> это
                      необходимо для основного функционала — генерации ответов
                      AI
                    </li>
                    <li>
                      <strong className="text-white">
                        Ограничение ответственности:
                      </strong>{" "}
                      Мы не несем ответственность за политику этих провайдеров.
                      Рекомендуем ознакомиться с их условиями обработки
                    </li>
                    <li>
                      <strong className="text-white">
                        Трансграничная передача:
                      </strong>{" "}
                      Передача данных может быть трансграничной — в рамках
                      Федерального закона 152‑ФЗ она регулируется ст. 12
                    </li>
                    <li>
                      Мы уведомляем Роскомнадзор и соблюдаем требования по
                      передаче в «адекватные» страны и получению данных об
                      уровнях защиты у иностранных организаций
                    </li>
                  </ul>
                </div>
              </Section>

              <Section id="user-rights" title="5. Права пользователя">
                <p className="mb-4">
                  Согласно{" "}
                  <strong className="text-white">статье 14 152‑ФЗ</strong>,
                  пользователь имеет права:
                </p>
                <div className="mb-6 grid grid-cols-1 gap-4 md:grid-cols-2">
                  <div className="rounded-lg bg-gray-800/30 p-4">
                    <h4 className="mb-2 font-semibold text-white">
                      🔍 Доступ к данным
                    </h4>
                    <p className="text-sm">
                      Получить информацию о ваших персональных данных
                    </p>
                  </div>
                  <div className="rounded-lg bg-gray-800/30 p-4">
                    <h4 className="mb-2 font-semibold text-white">
                      ✏️ Уточнение данных
                    </h4>
                    <p className="text-sm">
                      Исправить неточные или неполные данные
                    </p>
                  </div>
                  <div className="rounded-lg bg-gray-800/30 p-4">
                    <h4 className="mb-2 font-semibold text-white">
                      🗑️ Удаление данных
                    </h4>
                    <p className="text-sm">
                      Блокировать или удалить данные (включая историю переписок)
                    </p>
                  </div>
                  <div className="rounded-lg bg-gray-800/30 p-4">
                    <h4 className="mb-2 font-semibold text-white">
                      🚫 Отзыв согласия
                    </h4>
                    <p className="text-sm">
                      Отозвать согласие на обработку персональных данных
                    </p>
                  </div>
                </div>
                <div className="rounded-lg border border-blue-800/50 bg-blue-900/20 p-4">
                  <h4 className="mb-3 font-semibold text-white">
                    Как реализовать свои права:
                  </h4>
                  <ul className="list-disc space-y-2 pl-5 text-sm">
                    <li>
                      <strong className="text-white">
                        Письменно на адрес:
                      </strong>{" "}
                      <Link
                        href="mailto:privacy@novioai.com"
                        className="text-blue-400 hover:underline"
                      >
                        privacy@novioai.com
                      </Link>{" "}
                      для доступа, блокировки, отзыва согласия
                    </li>
                  </ul>
                </div>
              </Section>

              <Section id="security" title="6. Безопасность данных">
                <p className="mb-4">
                  Оператор применяет правовые, организационные и технические
                  меры в соответствии с{" "}
                  <strong className="text-white">ст. 19 152‑ФЗ</strong>:
                </p>
                <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                  <div className="rounded-lg bg-gray-800/30 p-4">
                    <h4 className="mb-2 flex items-center gap-2 font-semibold text-white">
                      <Shield className="h-4 w-4" />
                      Разграничение доступа
                    </h4>
                    <p className="text-sm">
                      Доступ к данным имеют только уполномоченные лица
                    </p>
                  </div>
                  <div className="rounded-lg bg-gray-800/30 p-4">
                    <h4 className="mb-2 flex items-center gap-2 font-semibold text-white">
                      <Eye className="h-4 w-4" />
                      Аудит безопасности
                    </h4>
                    <p className="text-sm">
                      Регулярные проверки систем безопасности
                    </p>
                  </div>
                  <div className="rounded-lg bg-gray-800/30 p-4">
                    <h4 className="mb-2 flex items-center gap-2 font-semibold text-white">
                      <FileText className="h-4 w-4" />
                      Резервное копирование
                    </h4>
                    <p className="text-sm">
                      Регулярное создание резервных копий данных
                    </p>
                  </div>
                </div>
              </Section>

              <Section id="children" title="7. Конфиденциальность детей">
                <div className="rounded-lg border border-red-800/50 bg-red-900/20 p-4">
                  <p className="flex items-start gap-2 text-red-300">
                    <AlertTriangle className="mt-0.5 h-5 w-5 flex-shrink-0" />
                    <span>
                      <strong>Возрастные ограничения:</strong> Сервис
                      предназначен исключительно для лиц старше 18 лет. Мы не
                      собираем и не обрабатываем личные данные детей младше 18
                      лет.
                    </span>
                  </p>
                </div>
              </Section>

              <Section id="changes" title="8. Уведомления об изменениях">
                <ul className="list-disc space-y-2 pl-6">
                  <li>Мы оставляем за собой право изменять данную Политику</li>
                  <li>
                    Новая редакция вступает в силу с момента публикации на
                    официальном сайте Сервиса
                  </li>
                  <li>
                    Пользователь обязан самостоятельно отслеживать актуальные
                    изменения. Продолжение использования Сервиса после
                    вступления изменений в силу означает согласие с новой
                    редакцией Политики
                  </li>
                </ul>
              </Section>

              <Section id="contact" title="9. Контактная информация">
                <p className="mb-4">
                  По всем вопросам, касающимся обработки персональных данных,
                  обращайтесь:
                </p>
                <div className="rounded-lg bg-gray-800/30 p-6 text-center">
                  <div className="mb-2 flex items-center justify-center gap-2">
                    <Mail className="h-5 w-5 text-blue-400" />
                    <Link
                      href="mailto:privacy@novioai.com"
                      className="font-medium text-blue-400 transition-colors hover:text-blue-300"
                    >
                      privacy@novioai.com
                    </Link>
                  </div>
                  <p className="text-sm text-gray-400">
                    Мы ответим в течение 24 часов
                  </p>
                </div>
              </Section>

              <div className="mt-12 border-t border-gray-800 pt-8 text-center">
                <p className="mb-2 text-lg font-semibold text-white">
                  Ваша конфиденциальность — наш приоритет
                </p>
                <p className="text-gray-400">
                  Мы серьезно относимся к защите ваших данных и соблюдению
                  российского законодательства
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Footer */}
      <footer className="border-t border-gray-800/50 bg-black py-16">
        <div className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 gap-8 md:grid-cols-4">
            <div className="md:col-span-2">
              <div className="mb-4 flex items-center gap-2">
                <div className="flex h-7 w-7 items-center justify-center rounded-lg bg-white">
                  <Sparkles className="h-4 w-4 text-black" />
                </div>
                <span className="text-lg font-semibold text-white">
                  NovioAI
                </span>
              </div>
              <p className="max-w-md text-sm text-gray-400">
                AI-ассистент для повышения продуктивности. Работайте умнее, а не
                усерднее.
              </p>
            </div>

            <div>
              <h3 className="mb-4 text-sm font-medium text-white">Компания</h3>
              <ul className="space-y-3 text-sm text-gray-400">
                <li>
                  <Link
                    href="/terms"
                    className="transition-colors hover:text-white"
                  >
                    Публичная оферта
                  </Link>
                </li>
                <li>
                  <Link
                    href="/privacy"
                    className="transition-colors hover:text-white"
                  >
                    Политика конфиденциальности
                  </Link>
                </li>
              </ul>
            </div>

            <div>
              <h3 className="mb-4 text-sm font-medium text-white">Контакты</h3>
              <ul className="space-y-3 text-sm text-gray-400">
                <li>
                  <a
                    href="mailto:support@novioai.com"
                    className="transition-colors hover:text-white"
                  >
                    support@novioai.com
                  </a>
                </li>
                <li>
                  <a
                    href="https://t.me/novioai"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="transition-colors hover:text-white"
                  >
                    Telegram
                  </a>
                </li>
              </ul>
            </div>
          </div>

          <div className="mt-12 flex flex-col items-center justify-between gap-4 border-t border-gray-800/50 pt-8 md:flex-row">
            <p className="text-center text-xs text-gray-500 md:text-left">
              © 2025 NovioAI. Все права защищены.
            </p>
            <div className="flex gap-6 text-xs">
              <Link
                href="/privacy"
                className="text-gray-500 transition-colors hover:text-gray-300"
              >
                Политика конфиденциальности
              </Link>
              <Link
                href="/terms"
                className="text-gray-500 transition-colors hover:text-gray-300"
              >
                Публичная оферта
              </Link>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default PrivacyPolicyPage;
